import axios from 'axios'
import { useAuthStore } from '../stores/auth'

// Axios factory for the client API (Api_Client). A fresh axios instance is
// built per call; interceptors are registered on that instance.
export default () => {
  const instance = axios.create({
    baseURL: process.env.NODE_ENV === 'production' ? `${window.location.origin}/api/` : `http://localhost:8081/api/`
  })

  // Request interceptor: attach the session token as a Bearer credential when
  // one is present. `useAuthStore()` is called lazily here (at request time,
  // not at module load) so the header always reflects the current session and
  // Pinia is guaranteed to be initialised (Requirements 5.1, 5.2, 5.3, 8.3,
  // 11.4). When no token is held, no `Authorization` header is added, so
  // unauthenticated requests (including the login request itself) carry none.
  instance.interceptors.request.use((config) => {
    const auth = useAuthStore()
    if (auth.token) {
      config.headers = config.headers || {}
      config.headers.Authorization = `Bearer ${auth.token}`
    }
    return config
  })

  // Response interceptor: a 403 returned to a request that carried an
  // `Authorization` header means the server rejected an authenticated session
  // (an expired/invalid JWT), so the session must be cleared and routed back to
  // the Login page (Requirement 7.1). A 403 to an unauthenticated request (e.g.
  // the login call itself, which carries no `Authorization` header) is left to
  // the caller as invalid credentials — it is NOT treated as expiry. The router
  // is imported lazily here to avoid a circular import at module-load time
  // (router → store → api → router). The error is always re-rejected so callers
  // still observe the failure.
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const headers = (error && error.config && error.config.headers) || {}
      const sentAuth = Object.keys(headers).some(
        (name) => name.toLowerCase() === 'authorization' && headers[name]
      )
      if (error && error.response && error.response.status === 403 && sentAuth) {
        import('../router').then((m) => m.handleSessionExpiry())
      }
      return Promise.reject(error)
    }
  )

  return instance
}
